import type { DlpLabelModel } from 'services/DlpConfiguration/DlpConfigurationService';

export type LabelIssueCode = 'blankId' | 'blankName' | 'duplicateId' | 'missingParent' | 'selfParent' | 'cycle' | 'tooDeep';
export interface LabelHierarchyIssue {
    code: LabelIssueCode;
    index: number;
    label: string;
    parent?: string;
}
export const labelParent = (label: DlpLabelModel) => label.parent_id || null;

export function validateDlpLabels(labels: DlpLabelModel[]): LabelHierarchyIssue[] {
    const issues: LabelHierarchyIssue[] = [];
    const counts = new Map<string, number>();
    const byId = new Map<string, DlpLabelModel>();
    labels.forEach(label => {
        counts.set(label.id, (counts.get(label.id) ?? 0) + 1);
        byId.set(label.id, label);
    });
    // Find cycle members in linear time, without recursion on user-supplied lists.
    const done = new Set<string>();
    const cycles = new Set<string>();
    labels.forEach(label => {
        let id: string | null = label.id;
        const path: string[] = [];
        const position = new Map<string, number>();
        while (id !== null && byId.has(id) && counts.get(id) === 1 && !done.has(id)) {
            if (position.has(id)) {
                path.slice(position.get(id)).forEach(member => cycles.add(member));
                break;
            }
            position.set(id, path.length);
            path.push(id);
            id = labelParent(byId.get(id)!);
        }
        path.forEach(member => done.add(member));
    });
    labels.forEach((label, index) => {
        const add = (code: LabelIssueCode) => issues.push({ code, index, label: label.id || `#${index + 1}`, parent: labelParent(label) ?? undefined });
        if (!label.id.trim()) add('blankId');
        if (!label.name.trim()) add('blankName');
        if (counts.get(label.id)! > 1) add('duplicateId');
        const parent = labelParent(label);
        if (parent === null) return;
        if (parent === label.id) add('selfParent');
        else if (!byId.has(parent) || counts.get(parent) !== 1) add('missingParent');
        else if (cycles.has(label.id)) add('cycle');
        else if (labelParent(byId.get(parent)!) !== null) add('tooDeep');
    });
    return issues;
}

export function groupDlpLabels(labels: DlpLabelModel[]) {
    const groups = labels.flatMap((label, rootIndex) => labelParent(label) === null ? [{ rootIndex, childIndices: [] as number[] }] : []);
    const counts = new Map<string, number>();
    labels.forEach(label => counts.set(label.id, (counts.get(label.id) ?? 0) + 1));
    const roots = new Map(groups.filter(group => counts.get(labels[group.rootIndex].id) === 1).map(group => [labels[group.rootIndex].id, group]));
    const unresolved: number[] = [];
    labels.forEach((label, index) => {
        const parent = labelParent(label);
        if (parent === null) return;
        const root = roots.get(parent);
        if (root) root.childIndices.push(index);
        else unresolved.push(index);
    });
    return { groups, unresolved };
}

export function renameDlpLabel(labels: DlpLabelModel[], index: number, patch: Partial<DlpLabelModel>): DlpLabelModel[] {
    const oldId = labels[index].id;
    const rename = patch.id !== undefined && patch.id.trim() !== '' && patch.id !== oldId && oldId !== '' && labels.filter(label => label.id === oldId).length === 1;
    return labels.map((label, i) => i === index ? { ...label, ...patch }
        : rename && labelParent(label) === oldId ? { ...label, parent_id: patch.id! } : label);
}

export function labelRemovalIndices(labels: DlpLabelModel[], index: number): Set<number> {
    const removed = new Set([index]);
    const id = labels[index].id;
    // A duplicate/empty ID must not cascade into unrelated rows.
    if (!id || labels.filter(label => label.id === id).length !== 1) return removed;
    const children = new Map<string, number[]>();
    labels.forEach((label, i) => {
        const parent = labelParent(label);
        if (parent !== null) {
            if (!children.has(parent)) children.set(parent, []);
            children.get(parent)!.push(i);
        }
    });
    const queue = [id];
    for (let i = 0; i < queue.length; i++) {
        for (const child of children.get(queue[i]) ?? []) {
            if (!removed.has(child)) {
                removed.add(child);
                queue.push(labels[child].id);
            }
        }
    }
    return removed;
}

export function moveDlpLabel(labels: DlpLabelModel[], index: number, direction: -1 | 1): DlpLabelModel[] {
    const parent = labelParent(labels[index]);
    if (parent === null) {
        const { groups, unresolved } = groupDlpLabels(labels);
        const position = groups.findIndex(group => group.rootIndex === index);
        const next = position + direction;
        if (next < 0 || next >= groups.length) return labels;
        [groups[position], groups[next]] = [groups[next], groups[position]];
        return [...groups.flatMap(group => [labels[group.rootIndex], ...group.childIndices.map(i => labels[i])]), ...unresolved.map(i => labels[i])];
    }
    const siblings = labels.flatMap((label, i) => labelParent(label) === parent ? [i] : []);
    const position = siblings.indexOf(index);
    const next = siblings[position + direction];
    if (next === undefined) return labels;
    const copy = [...labels];
    [copy[index], copy[next]] = [copy[next], copy[index]];
    return copy;
}
